'use client';

import { useEffect, useState, memo, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Stack,
  Collapse,
  Button,
  Switch,
  FormControlLabel,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DimensionsInput from '@/components/PriceEstimator/components/DimensionsInput';
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Route as RouteIcon,
} from '@mui/icons-material';

const CLUSTER_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

import DeliveryAddressAccordion from './DeliveryAddressAccordion';
import ParcelTypeSelector from '@/components/PriceEstimator/components/ParcelTypeSelector';
import BulkBadge from '@/components/BulkBadge';
import { useTranslation } from '@/kit/useTranslation';

const BUSINESS_COLORS = {
  primary: '#1a1a1a',
  accent: '#f59e0b',
  success: '#10b981',
};

const CATEGORIES = [
  { value: 'GENERAL', labelKey: 'bulkBooking.categoryGeneral' },
  { value: 'DOCUMENTS', labelKey: 'bulkBooking.categoryDocuments' },
  { value: 'ELECTRONICS', labelKey: 'bulkBooking.categoryElectronics' },
  { value: 'FRAGILE', labelKey: 'bulkBooking.categoryFragile' },
  { value: 'FOOD', labelKey: 'bulkBooking.categoryFood' },
  { value: 'CLOTHING', labelKey: 'bulkBooking.categoryClothing' },
  { value: 'MEDICAL', labelKey: 'bulkBooking.categoryMedical' },
];

function BulkBookingRowMobile({
  item,
  index,
  primaryPickup,
  onUpdate,
  onDelete,
  onDuplicate,
  bulkOrder,
  showBulkBadge = true,
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const [localWeight, setLocalWeight] = useState(item.weightKg || 1);
  const [localVolume, setLocalVolume] = useState(item.volumeCbm || 0);
  const [volumeDims, setVolumeDims] = useState(
    item.volume || { length: '', breadth: '', height: '' }
  );

  const handleVolumeChangeMobile = (vol) => setVolumeDims(vol);

  const handleApplyVolumeMobile = () => {
    const { length = 0, breadth = 0, height = 0 } = volumeDims || {};
    const l = Number(length) || 0;
    const b = Number(breadth) || 0;
    const h = Number(height) || 0;
    const cbm = l && b && h ? (l * b * h) / 1000000 : 0;
    setLocalVolume(cbm);
    onUpdate(item.itemId, {
      volumeCbm: cbm,
      volume: { length: l, breadth: b, height: h },
    });
  };

  // Category / parcel types for mobile
  const [selectedTypesMobile, setSelectedTypesMobile] = useState(
    item.parcelType && Array.isArray(item.parcelType) && item.parcelType.length > 0
      ? item.parcelType
      : item.category
        ? [item.category]
        : []
  );

  // Sync mobile selected types from incoming item updates
  useEffect(() => {
    const fromItem =
      item.parcelType && Array.isArray(item.parcelType) && item.parcelType.length > 0
        ? item.parcelType
        : item.category
          ? [item.category]
          : [];
    setSelectedTypesMobile(fromItem);
  }, [item.parcelType, item.category]);

  const getParcelTypeLabel = (parcelTypeArr, category) => {
    const arr =
      parcelTypeArr && Array.isArray(parcelTypeArr) && parcelTypeArr.length
        ? [...parcelTypeArr]
        : category
          ? [category]
          : [];
    if (!arr || arr.length === 0) return t('bulkBooking.other');

    // If there are any non-GENERAL types, ignore GENERAL entirely for the displayed label
    const nonGeneral = arr.filter((t) => t && t.toUpperCase() !== 'GENERAL');
    const source = nonGeneral.length ? nonGeneral : arr;

    // Deduplicate while preserving order
    const uniq = [...new Set(source.map((t) => (t ? t : '').toString()))].filter(Boolean);

    if (uniq.length <= 2) return uniq.join(', ');
    return `${uniq.slice(0, 2).join(', ')} +${uniq.length - 2}`;
  };
  const [showCategoryEditorMobile, setShowCategoryEditorMobile] = useState(false);

  const handleToggleMobile = (type) => {
    setSelectedTypesMobile((prev) => {
      const exists = prev.includes(type);
      if (exists) return prev.filter((t) => t !== type);
      return [...prev, type];
    });
  };

  const applyTypesMobile = () => {
    const primary =
      (selectedTypesMobile &&
        selectedTypesMobile.find((t) => t && t.toUpperCase() !== 'GENERAL')) ||
      (selectedTypesMobile && selectedTypesMobile[0]) ||
      'GENERAL';
    onUpdate(item.itemId, {
      parcelType: selectedTypesMobile,
      category: primary,
    });
    setShowCategoryEditorMobile(false);
  };

  const formatAddress = (address) => {
    if (!address) return t('bulkBooking.notSet');
    let addr;
    if (typeof address === 'string') {
      addr = address;
    } else {
      addr =
        address.fullAddress ||
        address.address ||
        address.formatted_address ||
        t('bulkBooking.notSet');
    }
    // Truncate to 50 chars to prevent UI stretching
    return addr.length > 50 ? addr.substring(0, 50) + '...' : addr;
  };

  const handleWeightBlur = useCallback(() => {
    if (localWeight !== item.weightKg) {
      onUpdate(item.itemId, { weightKg: localWeight });
    }
  }, [localWeight, item.itemId, item.weightKg, onUpdate]);

  const handleVolumeBlur = useCallback(() => {
    if (localVolume !== item.volumeCbm) {
      onUpdate(item.itemId, { volumeCbm: localVolume });
    }
  }, [localVolume, item.itemId, item.volumeCbm, onUpdate]);

  const handleCategoryChange = useCallback(
    (e) => {
      onUpdate(item.itemId, { category: e.target.value });
    },
    [item.itemId, onUpdate]
  );

  const handleUsePrimaryToggle = useCallback(
    (e) => {
      onUpdate(item.itemId, { usePrimaryPickup: e.target.checked });
    },
    [item.itemId, onUpdate]
  );

  const handleDeliveryUpdate = useCallback(
    (addressData) => {
      // Pass full address object to backend (matching individual parcel format)
      onUpdate(item.itemId, {
        deliveryAddress: addressData.addressObject, // Full object with type, latitude, longitude, formatted_address
        recipientName: addressData.recipientName,
        recipientPhone: addressData.recipientPhone,
        saveDeliveryAddress: addressData.saveDeliveryAddress,
      });
      setShowDeliveryForm(false);
    },
    [item.itemId, onUpdate]
  );

  // Check if delivery address exists (address can be object or have lat/lng)
  const hasDelivery =
    item.deliveryAddress &&
    ((typeof item.deliveryAddress === 'object' && item.deliveryAddress.latitude) ||
      item.deliveryLat);

  // Distance: backend value if available, else compute from coordinates
  const displayDistanceKm = useMemo(() => {
    if (item.distanceKm) return Number(item.distanceKm).toFixed(1);
    const pickupLat = primaryPickup?.lat;
    const pickupLng = primaryPickup?.lng;
    const deliveryLat = item.deliveryLat || item.deliveryAddress?.latitude;
    const deliveryLng = item.deliveryLng || item.deliveryAddress?.longitude;
    if (pickupLat && pickupLng && deliveryLat && deliveryLng) {
      return haversineKm(
        Number(pickupLat),
        Number(pickupLng),
        Number(deliveryLat),
        Number(deliveryLng)
      ).toFixed(1);
    }
    return null;
  }, [
    item.distanceKm,
    item.deliveryLat,
    item.deliveryLng,
    item.deliveryAddress,
    primaryPickup?.lat,
    primaryPickup?.lng,
  ]);

  const parcelLabelMobile = getParcelTypeLabel(item.parcelType, item.category);
  const displayLabelMobile =
    typeof parcelLabelMobile === 'string' && parcelLabelMobile.toUpperCase() === 'GENERAL'
      ? t('bulkBooking.categoryGeneral')
      : parcelLabelMobile === t('bulkBooking.other')
        ? item.category || t('bulkBooking.categoryGeneral')
        : parcelLabelMobile;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 3,
        border:
          item.clusterId != null
            ? `1px solid ${alpha(CLUSTER_COLORS[(item.clusterId - 1) % CLUSTER_COLORS.length], 0.3)}`
            : '1px solid #e2e8f0',
        overflow: 'hidden',
        position: 'relative',
        transition: 'transform 200ms ease, box-shadow 200ms ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor:
            item.clusterId != null
              ? CLUSTER_COLORS[(item.clusterId - 1) % CLUSTER_COLORS.length]
              : hasDelivery
                ? BUSINESS_COLORS.success
                : BUSINESS_COLORS.accent,
        },
      }}
    >
      {/* Cluster route banner (visible for all clustered items after pricing is calculated) */}
      {item.clusterId != null &&
        (() => {
          const clusterColor = CLUSTER_COLORS[(item.clusterId - 1) % CLUSTER_COLORS.length];
          return (
            <Box
              sx={{
                px: 1.5,
                py: 0.4,
                bgcolor: alpha(clusterColor, 0.08),
                borderBottom: `1px solid ${alpha(clusterColor, 0.15)}`,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <RouteIcon sx={{ fontSize: 12, color: clusterColor }} />
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: clusterColor,
                  letterSpacing: 0.5,
                }}
              >
                ROUTE {item.clusterId}
                {item.isMultiStop ? ' · shared route' : ' · solo'}
              </Typography>
            </Box>
          );
        })()}
      {/* Header */}
      <Box
        sx={{
          p: { xs: 1, md: 2 },
          pl: { xs: 2, md: 3 },
          pr: 2,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#fafafa',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Typography
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: BUSINESS_COLORS.primary,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.95rem',
            }}
          >
            {index + 1}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: BUSINESS_COLORS.primary,
                fontSize: { xs: '0.95rem', md: '0.98rem' },
                maxWidth: { xs: 190, sm: 280 },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {hasDelivery
                ? formatAddress(item.deliveryAddress)
                : t('bulkBooking.addDeliveryAddress')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip
                label={
                  Array.isArray(item.parcelType) && item.parcelType.length > 0
                    ? item.parcelType.length === 1
                      ? item.parcelType[0]
                      : `${item.parcelType[0]}+`
                    : item.category || 'Other'
                }
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(BUSINESS_COLORS.accent, 0.08),
                  color: BUSINESS_COLORS.accent,
                }}
              />
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                {item.weightKg}kg
              </Typography>
            </Stack>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {item.itemPrice ? (
            <Box
              sx={{
                bgcolor: BUSINESS_COLORS.success,
                color: 'white',
                px: 1.25,
                py: 0.5,
                borderRadius: 1.5,
                fontWeight: 800,
              }}
            >
              {Math.round(item.itemPrice)}
            </Box>
          ) : (
            <Box
              sx={{
                bgcolor: alpha(BUSINESS_COLORS.accent, 0.1),
                color: BUSINESS_COLORS.accent,
                px: 1.25,
                py: 0.5,
                borderRadius: 1.5,
                fontWeight: 700,
              }}
            >
              {t('bulkBooking.noPrice')}
            </Box>
          )}

          {showBulkBadge && bulkOrder && bulkOrder.bulkOrderReference && (
            <Box sx={{ ml: 0.5 }} onClick={(e) => e.stopPropagation()}>
              <BulkBadge
                compact
                bulkOrderId={bulkOrder.bulkOrderId}
                bulkOrderReference={bulkOrder.bulkOrderReference}
                itemCount={bulkOrder.itemCount}
              />
            </Box>
          )}

          <IconButton
            size="small"
            sx={{ ml: 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            aria-label="expand"
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Mobile quick actions (Edit Delivery / Duplicate / Delete) */}
      <Box
        sx={{
          p: 1,
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>
            {t('bulkBooking.deliveryLabel')}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9rem',
              color: BUSINESS_COLORS.primary,
              maxWidth: 220,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {hasDelivery ? formatAddress(item.deliveryAddress) : t('bulkBooking.noDeliveryAddress')}
          </Typography>
          {displayDistanceKm && (
            <Typography sx={{ fontSize: '0.65rem', color: '#10b981', fontWeight: 600, mt: 0.25 }}>
              ~{displayDistanceKm} km
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeliveryForm(true);
            }}
          >
            {t('bulkBooking.edit')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(item.itemId);
            }}
          >
            {t('bulkBooking.duplicate')}
          </Button>
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item.itemId);
            }}
          >
            {t('bulkBooking.delete')}
          </Button>
        </Stack>
      </Box>

      {/* Expanded content */}
      <Collapse in={isExpanded}>
        <Box sx={{ p: 2, pl: 3, borderTop: '1px solid #e2e8f0' }}>
          {/* Pickup toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={item.usePrimaryPickup !== false}
                onChange={handleUsePrimaryToggle}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: BUSINESS_COLORS.accent,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    bgcolor: BUSINESS_COLORS.accent,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.8rem' }}>
                {t('bulkBooking.usePrimaryAddress')}
              </Typography>
            }
            sx={{ mb: 2 }}
          />

          {/* Pickup address display */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(BUSINESS_COLORS.success, 0.05),
            }}
          >
            <LocationIcon sx={{ fontSize: 18, color: BUSINESS_COLORS.success, mt: 0.2 }} />
            <Box>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748b', mb: 0.25 }}>
                {t('bulkBooking.pickupLabel')}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: BUSINESS_COLORS.primary }}>
                {item.usePrimaryPickup !== false
                  ? formatAddress(primaryPickup?.address)
                  : formatAddress(item.pickupAddress)}
              </Typography>
            </Box>
          </Box>

          {/* Delivery address */}
          <Box
            onClick={() => setShowDeliveryForm(true)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: hasDelivery ? alpha('#ef4444', 0.05) : alpha(BUSINESS_COLORS.accent, 0.05),
              border: hasDelivery ? 'none' : `1px dashed ${BUSINESS_COLORS.accent}`,
              cursor: 'pointer',
            }}
          >
            <LocationIcon
              sx={{
                fontSize: 18,
                color: hasDelivery ? '#ef4444' : '#94a3b8',
                mt: 0.2,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748b', mb: 0.25 }}>
                {t('bulkBooking.deliveryLabel')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  color: hasDelivery ? BUSINESS_COLORS.primary : '#94a3b8',
                }}
              >
                {hasDelivery
                  ? formatAddress(item.deliveryAddress)
                  : t('bulkBooking.tapToAddDeliveryAddress')}
              </Typography>
              {item.recipientName && (
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.5 }}>
                  {item.recipientName} {item.recipientPhone && `• ${item.recipientPhone}`}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Weight, Volume, and Category */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              label={t('bulkBooking.weight')}
              type="number"
              size="small"
              value={localWeight}
              onChange={(e) => setLocalWeight(parseFloat(e.target.value) || 0)}
              onBlur={handleWeightBlur}
              inputProps={{ min: 0.1, max: 100, step: 0.1 }}
              sx={{ width: { xs: '100%', sm: '50%' } }}
              InputProps={{
                endAdornment: (
                  <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>kg</Typography>
                ),
              }}
            />
            <TextField
              label={t('bulkBooking.volume')}
              type="number"
              size="small"
              value={localVolume}
              onChange={(e) => setLocalVolume(parseFloat(e.target.value) || 0)}
              onBlur={handleVolumeBlur}
              inputProps={{ min: 0, max: 10, step: 0.01 }}
              sx={{ width: { xs: '100%', sm: '50%' } }}
              InputProps={{
                endAdornment: (
                  <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>m³</Typography>
                ),
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={displayLabelMobile}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(BUSINESS_COLORS.accent, 0.08),
                  color: BUSINESS_COLORS.accent,
                }}
              />

              {showBulkBadge && bulkOrder && bulkOrder.bulkOrderReference && (
                <BulkBadge
                  bulkOrderId={bulkOrder.bulkOrderId}
                  bulkOrderReference={bulkOrder.bulkOrderReference}
                  itemCount={bulkOrder.itemCount}
                  compact={true}
                />
              )}

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCategoryEditorMobile((s) => !s);
                }}
              >
                {showCategoryEditorMobile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Stack>

          {/* Category editor (mobile) */}
          {showCategoryEditorMobile && (
            <Box sx={{ mb: 2 }}>
              <ParcelTypeSelector
                selectedTypes={selectedTypesMobile}
                onToggle={handleToggleMobile}
                compact={true}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={applyTypesMobile}
                  sx={{ textTransform: 'none' }}
                >
                  {t('bulkBooking.apply')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowCategoryEditorMobile(false)}
                  sx={{ textTransform: 'none' }}
                >
                  {t('bulkBooking.cancel')}
                </Button>
              </Stack>
            </Box>
          )}

          {/* Dimensions editor (mobile) */}
          <Box sx={{ mb: 2 }}>
            <DimensionsInput
              volume={volumeDims}
              onChange={handleVolumeChangeMobile}
              compact={true}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={handleApplyVolumeMobile} sx={{ textTransform: 'none' }}>
                {t('bulkBooking.apply')}
              </Button>
            </Stack>
          </Box>

          {/* Action buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
            <Button
              size="medium"
              fullWidth
              startIcon={<CopyIcon />}
              onClick={() => onDuplicate(item.itemId)}
              sx={{ color: '#64748b', textTransform: 'none' }}
            >
              {t('bulkBooking.duplicate')}
            </Button>
            <Button
              size="medium"
              fullWidth
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(item.itemId)}
              sx={{ color: '#ef4444', textTransform: 'none' }}
            >
              {t('bulkBooking.delete')}
            </Button>
          </Stack>
        </Box>
      </Collapse>

      {/* Delivery address form: Dialog on small screens, inline collapse on larger */}
      {isSmall ? (
        <Dialog
          open={showDeliveryForm}
          onClose={() => setShowDeliveryForm(false)}
          fullWidth
          fullScreen
        >
          <DialogTitle>{t('bulkBooking.deliveryDetailsTitle')}</DialogTitle>
          <DialogContent dividers>
            <DeliveryAddressAccordion
              item={item}
              onSave={(data) => {
                handleDeliveryUpdate(data);
                setShowDeliveryForm(false);
              }}
              onCancel={() => setShowDeliveryForm(false)}
            />
          </DialogContent>
        </Dialog>
      ) : (
        <Collapse in={showDeliveryForm}>
          <Box sx={{ borderTop: '1px solid #e2e8f0' }}>
            <DeliveryAddressAccordion
              item={item}
              onSave={(data) => {
                handleDeliveryUpdate(data);
                setShowDeliveryForm(false);
              }}
              onCancel={() => setShowDeliveryForm(false)}
            />
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}

export default memo(BulkBookingRowMobile);
